import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import MLBHeaderStats from '../components/MLBHeaderStats';
import MLBFilterSection from '../components/MLBFilterSection';
import MLBConfidenceTiers from '../components/MLBConfidenceTiers';
const MLBDashboard = () => {
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        pitcher: ''
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                const response = await fetch(`/static/mlb/mlb_predictions.json?v=${timestamp}&debug=true`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('DEBUG - Fetching MLB predictions with timestamp:', timestamp);
                if (!response.ok) {
                    throw new Error('Failed to fetch MLB predictions');
                }
                const data = await response.json();
                console.log('DEBUG - Loaded predictions data:', data);
                const padresGame = data.predictions.find(g => g.game_info?.home_team?.includes('Padres'));
                if (padresGame) {
                    console.log('DEBUG - Padres game data:', {
                        confidence_level: padresGame.recommended_bet?.confidence_level,
                        ensemble_prediction: padresGame.ensemble_prediction,
                        ensemble_probability: padresGame.ensemble_probability,
                        prediction_confidence: padresGame.prediction_confidence
                    });
                }
                setPredictions(data);
                setFilteredGames(data.predictions);
            }
            catch (err) {
                setError('Failed to load MLB predictions. Please try again later.');
                console.error('Error loading MLB predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, []);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.predictions;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter(game => (game.game_info?.home_team || game.home_team)?.toLowerCase().includes(teamLower) ||
                (game.game_info?.away_team || game.away_team)?.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter - show all games including insufficient value ones
        if (filters.confidence !== 'ALL') {
            if (filters.confidence === 'NO_BET') {
                // Show games that don't meet professional betting criteria (insufficient value)
                filtered = filtered.filter(game => game.professional_bet_decision?.should_bet !== true ||
                    (game.recommended_bet?.bet === 'NO_BET') ||
                    (game.betting_analysis?.best_bet_recommendation?.recommendation === 'NO_BET'));
            }
            else {
                // Show all games with the selected confidence level (including insufficient value)
                filtered = filtered.filter(game => {
                    // Map new confidence tiers to old confidence levels for filtering
                    let confidenceLevel = game.recommended_bet?.confidence_level;
                    // Check new structure
                    const confidenceTier = game.betting_analysis?.best_bet_recommendation?.confidence_tier;
                    if (confidenceTier === 'HIGH_CONFIDENCE_BET') {
                        confidenceLevel = 'HIGH';
                    }
                    else if (confidenceTier === 'MODERATE_CONFIDENCE_BET') {
                        confidenceLevel = 'MEDIUM';
                    }
                    else if (confidenceTier === 'LOW_CONFIDENCE_BET') {
                        confidenceLevel = 'LOW';
                    }
                    return confidenceLevel === filters.confidence;
                });
            }
        }
        // Apply pitcher filter
        if (filters.pitcher) {
            const pitcherLower = filters.pitcher.toLowerCase();
            filtered = filtered.filter(game => (game.game_info?.home_pitcher_name || game.home_pitcher)?.toLowerCase().includes(pitcherLower) ||
                (game.game_info?.away_pitcher_name || game.away_pitcher)?.toLowerCase().includes(pitcherLower));
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No MLB predictions available at this time." }) }));
    }
    // Create metadata object from the root-level data
    const metadata = {
        generated_at: predictions.system_status?.generated_timestamp || predictions.generated_at || predictions.prediction_date || new Date().toISOString(),
        date: predictions.date || predictions.prediction_date || new Date().toISOString().split('T')[0],
        total_games: predictions.total_games || predictions.games_count || 0,
        model_training_date: predictions.date || predictions.prediction_date || new Date().toISOString().split('T')[0],
        system_status: predictions.system_status, // Pass through the entire system_status
        models_used: ['random_forest', 'xgboost', 'lightgbm', 'ensemble'],
        features_used: ['pitcher_era', 'pitcher_strikeouts', 'team_performance'],
        training_games: 1000, // Placeholder
        model_performance: {
            random_forest: { accuracy: 0.65, auc: 0.72 },
            xgboost: { accuracy: 0.67, auc: 0.74 },
            lightgbm: { accuracy: 0.66, auc: 0.73 },
            ensemble: { accuracy: 0.68, auc: 0.75 }
        },
        feature_importance: [
            { feature: 'pitcher_era', importance: 0.35 },
            { feature: 'team_performance', importance: 0.25 },
            { feature: 'pitcher_strikeouts', importance: 0.20 }
        ]
    };
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(MLBHeaderStats, { metadata: metadata, totalGames: predictions.total_games || predictions.games_count || 0, games: predictions.predictions }), _jsx(MLBFilterSection, { filters: filters, onFilterChange: handleFilterChange, games: predictions.predictions }), _jsx(MLBConfidenceTiers, { games: filteredGames })] }));
};
export default MLBDashboard;
